import {
  Alert,
  Button,
  EmptyState,
  List,
  ListRow,
  Section,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { errorMessage } from '../../../lib/errors';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useConnections, useRevokeConnection, type Connection } from '../api';

/** The MCP endpoint of this instance; the web app is served from the same origin. */
function mcpUrl(): string {
  return `${window.location.origin}/mcp`;
}

/**
 * Connected apps (MCP-005): AI assistants the member allowed to use swarm,
 * with what they may do and when they were used. Disconnecting ends their
 * access at once.
 */
export function ConnectionsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const connections = useConnections();
  const revoke = useRevokeConnection();
  const [confirm, setConfirm] = useState<Connection | null>(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(mcpUrl());
      toast.show(t('connections.copied'));
    } catch {
      // Clipboard not available (e.g. insecure context): the address stays visible.
    }
  };

  return (
    <Stack gap={6}>
      {connections.error && <Alert tone="danger">{errorMessage(connections.error)}</Alert>}
      {connections.isPending && <Spinner label={t('common.loading')} />}
      {connections.data &&
        (connections.data.length === 0 ? (
          <EmptyState title={t('connections.emptyTitle')} />
        ) : (
          <Section title={t('connections.title')}>
            <List>
              {connections.data.map((c) => (
                <ListRow
                  key={c.id}
                  title={c.client_name}
                  description={[
                    c.scopes.includes('write') ? t('connections.readWrite') : t('connections.read'),
                    t('connections.since', { date: formatDate(c.created_at) }),
                    c.last_used_at
                      ? t('connections.lastUsed', { date: formatDateTime(c.last_used_at) })
                      : t('connections.neverUsed'),
                  ].join(' · ')}
                  trailing={
                    <Button
                      variant="plain"
                      size="sm"
                      aria-label={`${t('connections.revoke')}: ${c.client_name}`}
                      onClick={() => setConfirm(c)}
                    >
                      {t('connections.revoke')}
                    </Button>
                  }
                />
              ))}
            </List>
          </Section>
        ))}
      <Section title={t('connections.connectTitle')} variant="content">
        <Stack gap={3}>
          <Text>{t('connections.connectText')}</Text>
          <Stack direction="row" gap={2} align="center" wrap>
            <Text as="span" weight="semibold">
              <code>{mcpUrl()}</code>
            </Text>
            <Button variant="secondary" size="sm" onClick={() => void copy()}>
              {t('connections.copy')}
            </Button>
          </Stack>
        </Stack>
      </Section>
      <ConfirmDialog
        open={confirm !== null}
        title={t('connections.revokeTitle', { client: confirm?.client_name ?? '' })}
        description={t('connections.revokeText')}
        confirmLabel={t('connections.revoke')}
        pending={revoke.isPending}
        error={revoke.error ? errorMessage(revoke.error) : undefined}
        onClose={() => {
          revoke.reset();
          setConfirm(null);
        }}
        onConfirm={() => {
          if (!confirm) return;
          const name = confirm.client_name;
          revoke.mutate(confirm.id, {
            onSuccess: () => {
              setConfirm(null);
              toast.show(t('connections.revoked', { client: name }));
            },
          });
        }}
      />
    </Stack>
  );
}
